import { useEffect, useRef } from "react";
import moment from "moment";
import { Badge, BadgeSize, BadgeVariant } from "shared/components";
import {
  Switch,
  SwitchSize,
  LabelPosition
} from "shared/components/forms/Switch";
import { type Register } from "shared/hooks";

interface WaitlistToggleProps {
  register: Register;
  occupied?: boolean | null;
  availableAt?: string | null;
  canChange: boolean;
}

// The Available-by date is a calendar date, so it counts as arrived on the day
// itself — matching the API's daily clearing job (`available_at <= now`).
const availableByArrived = (availableAt?: string | null): boolean => {
  if (!availableAt) return false;
  const date = moment(String(availableAt));
  return date.isValid() && !date.isAfter(moment(), "day");
};

export const WaitlistToggle = ({
  register,
  occupied,
  availableAt,
  canChange
}: WaitlistToggleProps) => {
  // Waitlist only applies to a unit that is still occupied with an Available-by
  // date that hasn't arrived yet.
  const qualifies =
    Boolean(occupied) &&
    Boolean(availableAt) &&
    !availableByArrived(availableAt);
  const value = Boolean(register("waitlist").value);
  const checked = qualifies && value;

  // Seeded with the qualifying state on mount so merely opening a unit never
  // flips the toggle — only a change the manager makes here does.
  const wasQualifying = useRef(qualifies);

  // The toggle follows Current status and Available by on its own: it switches on
  // as the unit starts qualifying (Occupied with a date still ahead) and off as
  // soon as it stops — status back to Available, date removed, or date arrived.
  // Both directions write the form value rather than just the rendered state,
  // otherwise a stale value would be submitted on save.
  //
  // On is fired only on the transition into qualifying, so a manager who
  // deliberately switches it back off keeps it off while editing instead of
  // fighting an effect that re-enables it on every render.
  useEffect(() => {
    const startedQualifying = qualifies && !wasQualifying.current;
    wasQualifying.current = qualifies;

    if (!canChange) return;
    if (startedQualifying && !value) {
      register("waitlist").onChange(true);
      return;
    }
    if (!qualifies && value) register("waitlist").onChange(false);
  }, [qualifies, value, canChange]);

  return (
    <div className="pt-4">
      <div className="flex items-center gap-2">
        <p className="text-sm font-bold text-content-1000">Waitlist</p>
        {checked ? (
          <Badge variant={BadgeVariant.SUCCESS} size={BadgeSize.SM}>
            Active now
          </Badge>
        ) : (
          <Badge
            variant={BadgeVariant.BASE}
            size={BadgeSize.SM}
            className="items-center"
            leftElement={
              <span className="size-1.5 shrink-0 self-center rounded-full bg-content-1000" />
            }
          >
            Inactive
          </Badge>
        )}
      </div>

      <div className="mt-2 flex items-start justify-between gap-6">
        <p className="max-w-[640px] text-sm text-content-300">
          While the unit is occupied, it stays listed as coming soon and
          prospects join the waitlist instead of booking tours.
        </p>
        <Switch
          size={SwitchSize.SM}
          position={LabelPosition.Right}
          checked={checked}
          disabled={!qualifies || !canChange}
          onChange={(value) => register("waitlist").onChange(value)}
        />
      </div>
    </div>
  );
};
